import { ConfigForData } from '@droplet-tech-code/core-common-types';
import { CheckBox } from '@droplet-tech-code/core-elements/module/ui/Checkbox';
import { dayjs } from '@droplet-tech-code/core-elements/module/utils/utils.date';
import { Divider } from '@droplet-tech-code/core-elements/module/ui/Divider/Divider';
import {
  FormBuilder,
  FormHandler,
} from '@droplet-tech-code/core-elements/module/ui/Inputs/Form';
import { Modal, useModal } from '@droplet-tech-code/core-elements/module/ui/Modal';
import { HStack, VStack } from '@droplet-tech-code/core-elements/module/ui/Stack';
import { showToast } from '@droplet-tech-code/core-elements/module/ui/Toast';
import { getErrorResponse } from '@droplet-tech-code/core-elements/module/utils/error';
import {
  PurchaseClientWithMetaWithTickets,
  SupportRequestBackendClient,
  SupportRequestCreatePaymentRefundRequestForm,
} from '@naus-code/naus-admin-types';
import { useEffect, useMemo, useRef, useState } from 'react';

import { FormContent } from '~/components/Form/Form.Container';
import { AppText } from '~/components/Text';
import { bookingApi } from '~/screens/Support/Bookings/Bookings.api';
import { getIdFromPurchase } from '~/screens/Support/Purchases/purchase.utils';
import { supportRequestActionsApi } from '~/screens/Support/Requests/supportRequestActions.api';
import { handleRTKResponse } from '~/utils/network.utils';
import { translate } from '~/utils/translation.utils';

import { safeMultiply } from '../utils/utils';

type AmendmentStatus = 'completed' | 'not-completed';

const purchaseSelectInputId = 'purchaseIds';

const getTripLabel = (purchase: PurchaseClientWithMetaWithTickets) => {
  const departure = `${dayjs(purchase.departureDate).format('DD/MM/YYYY')} ${purchase.departureTime}`;
  const route = `${purchase.originDisplay} (${purchase.origin}) → ${purchase.destinationDisplay} (${purchase.destination})`;
  return `${departure} ${route}`;
};

/**
 * The trips being refunded are only known once the amendment is done, so the
 * selector is added to the form for a completed amendment and left out
 * otherwise — an input that is not rendered is not registered on the form, so
 * while it is out its value is neither validated nor submitted.
 *
 * Any purchase selector coming down with the server config is dropped so this
 * stays the only source of the `purchaseIds` value.
 */
const withPurchaseSelect = ({
  config,
  showPurchaseSelect,
  purchases,
}: {
  config: ConfigForData;
  showPurchaseSelect: boolean;
  purchases: PurchaseClientWithMetaWithTickets[];
}): ConfigForData => {
  const configWithoutPurchaseSelect = config.map((row) => {
    if (!('items' in row) || !row.items?.some((i) => i.id === purchaseSelectInputId)) {
      return row;
    }

    return {
      ...row,
      // The row label is its section header on mobile — only safe to drop when
      // the purchase selector is the sole input on the row.
      label: row.items.length === 1 ? undefined : row.label,
      // Swapped rather than removed so the row count stays stable and the
      // inputs around it are not remounted.
      items: row.items.map((item) =>
        item.id === purchaseSelectInputId
          ? { id: `blank-${purchaseSelectInputId}`, type: 'none' as const }
          : item,
      ),
    };
  });

  if (!showPurchaseSelect || purchases.length === 0) {
    return configWithoutPurchaseSelect;
  }

  const purchaseIds = purchases.map(getIdFromPurchase);

  return [
    ...configWithoutPurchaseSelect,
    {
      items: [
        {
          id: purchaseSelectInputId,
          type: 'multi-select',
          label: translate('supportRequests.refund.selectTrips'),
          list: purchases.map((purchase, index) => ({
            value: purchaseIds[index],
            label: getTripLabel(purchase),
          })),
          // Nothing to choose from with a single trip — take it as given.
          initialValue: purchaseIds.length === 1 ? purchaseIds : undefined,
        },
      ],
    },
  ];
};

export const CreatePaymentRefundKey = 'AddModal-CreateRefund';
export const CreatePaymentRefundModal = ({
  supportRequestInfo,
  check,
}: {
  supportRequestInfo: SupportRequestBackendClient;
  check: string;
}) => {
  const form = useRef(new FormHandler());
  const { closeModal } = useModal();
  const supportId = supportRequestInfo._id;

  const [amendmentStatus, setAmendmentStatus] = useState<AmendmentStatus>();
  const showPurchaseSelect = amendmentStatus === 'completed';

  const [createPendingPaymentRefund, { isLoading: isCreatingPaymentRefund }] =
    supportRequestActionsApi.useCreatePendingPaymentRefundMutation();

  const { data: paymentCreateConfig, error: paymentCreateConfigError } =
    supportRequestActionsApi.useCreatePaymentRefundFormConfigQuery(supportId);

  // Without the form config there is nothing to fill in, so drop the modal
  // and surface why rather than leaving an empty sheet open.
  useEffect(() => {
    if (paymentCreateConfigError) {
      closeModal(CreatePaymentRefundKey);
      showToast(getErrorResponse(paymentCreateConfigError).message, {
        type: 'error',
      });
    }
  }, [paymentCreateConfigError]);

  // The refund form config includes companyValue and a readonly companyCcyRate.
  // Entering a company-ccy refund auto-fills the booking-ccy value at that rate;
  // the value field stays independently editable afterwards. Rounded to 2dp —
  // the server rejects sub-cent precision.
  // Number inputs emit raw display text to subscribers, hence the string parse.
  useEffect(() => {
    return form.current.subscribeToValueChange((id, val) => {
      if (id !== 'companyValue') return;
      const companyValue = Number(String(val ?? '').replace(/,/g, ''));
      if (Number.isNaN(companyValue)) return;
      const rate =
        Number(
          form.current.getSingleValue({
            id: 'companyCcyRate',
            validate: false,
            showFeedback: false,
          }),
        ) || 1;
      form.current.setInputValue(
        'value',
        String(Math.round(companyValue * rate * 100) / 100),
      );
    });
  }, []);

  if (paymentCreateConfig === undefined) {
    return null;
  }

  return (
    <Modal.Container>
      <Modal.Header
        title={translate('supportRequests.createRefund')}
        subtitle={translate('supportRequests.enterRefundDetails')}
        divider
        onPress={() => {
          closeModal(CreatePaymentRefundKey);
        }}
      />
      <Modal.Body p="0">
        <VStack flex={1}>
          <AmendmentStatusSelect
            amendmentStatus={amendmentStatus}
            onChange={setAmendmentStatus}
          />
          <Divider />
          <CreateRefundForm
            supportId={supportId}
            bookingId={supportRequestInfo.bookingId}
            form={form.current}
            showPurchaseSelect={showPurchaseSelect}
          />
        </VStack>
      </Modal.Body>
      <Modal.Footer
        divider
        isLoading={isCreatingPaymentRefund}
        confirmProps={{ disabled: !amendmentStatus }}
        onConfirm={async () => {
          if (!amendmentStatus) {
            return;
          }

          const { valid, value } =
            form.current.getValue<SupportRequestCreatePaymentRefundRequestForm>();

          if (valid) {
            const response = await createPendingPaymentRefund({
              supportId,
              check,
              description: value.description,
              purchaseIds: showPurchaseSelect ? (value.purchaseIds ?? []) : [],
              paymentSessionDetails: {
                paymentSessionId: value.paymentSessionId,
                // The form takes the refund in major units, the request
                // expects minor units.
                value: safeMultiply(value.value ?? 0, 100),
              },
            });
            handleRTKResponse({
              response,
              onSuccess: () => {
                closeModal(CreatePaymentRefundKey);
              },
            });
          }
        }}
        onCancel={() => {
          closeModal(CreatePaymentRefundKey);
        }}
      />
    </Modal.Container>
  );
};

export const useCreatePaymentRefund = () => {
  const { openModal } = useModal();

  return {
    openModal: ({
      supportRequestInfo,
      check,
    }: {
      supportRequestInfo: SupportRequestBackendClient;
      check: string;
    }) => {
      openModal(
        <CreatePaymentRefundModal
          supportRequestInfo={supportRequestInfo}
          check={check}
        />,
        {
          key: CreatePaymentRefundKey,
          type: 'action-sheet-full',
        },
      );
    },
  };
};

const AmendmentStatusSelect = ({
  amendmentStatus,
  onChange,
}: {
  amendmentStatus: AmendmentStatus | undefined;
  onChange: (amendmentStatus: AmendmentStatus | undefined) => void;
}) => {
  return (
    <HStack p="3" space="6">
      <HStack space="2.5" align="center">
        <CheckBox
          checked={amendmentStatus === 'completed'}
          onPress={(isChecked) => onChange(isChecked ? 'completed' : undefined)}
        />
        <AppText.Body2Medium color="success-mid">
          {translate('supportRequests.refund.amendmentCompleted')}
        </AppText.Body2Medium>
      </HStack>
      <HStack space="2.5" align="center">
        <CheckBox
          checked={amendmentStatus === 'not-completed'}
          onPress={(isChecked) => onChange(isChecked ? 'not-completed' : undefined)}
        />
        <AppText.Body2Medium color="error-mid">
          {translate('supportRequests.refund.amendmentNotCompleted')}
        </AppText.Body2Medium>
      </HStack>
    </HStack>
  );
};

const CreateRefundForm = ({
  supportId,
  bookingId,
  form,
  showPurchaseSelect,
}: {
  supportId: string;
  bookingId: string | undefined;
  form: FormHandler;
  showPurchaseSelect: boolean;
}) => {
  const { data: paymentCreateConfig, isLoading: isLoadingPaymentConfig } =
    supportRequestActionsApi.useCreatePaymentRefundFormConfigQuery(supportId);

  const { data: bookingData } = bookingApi.useGetBookingQuery(bookingId ?? '', {
    skip: !bookingId,
  });

  const purchases = bookingData?.booking.purchases;

  const config = useMemo(
    () =>
      paymentCreateConfig
        ? withPurchaseSelect({
            config: paymentCreateConfig,
            showPurchaseSelect,
            purchases: purchases ?? [],
          })
        : undefined,
    [paymentCreateConfig, showPurchaseSelect, purchases],
  );

  return (
    <FormContent isLoading={isLoadingPaymentConfig} form={config} flex={1}>
      {config ? <FormBuilder data={config} form={form} /> : null}
    </FormContent>
  );
};
